import React, { useState, useEffect, useMemo } from "react";
import { 
  Folder, 
  Package, 
  Search, 
  ArrowLeft, 
  TrendingUp, 
  DollarSign, 
  Layers, 
  CheckCircle2, 
  AlertCircle, 
  ArrowRight,
  ShoppingBag,
  RefreshCw,
  Box
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { useCatalogContext } from "../../contexts/CatalogContext";
import { getBaseSku, cleanSku } from "../../utils/skuUtils";
import { formatMoney } from "../../utils/formatters";
import TaxonomiaManagerModal from "../modals/TaxonomiaManagerModal";

/**
 * TelaCategoria.js — Tela Agregadora de Categoria (Fase P4.1)
 *
 * Agrega produtos de uma categoria temática (ex: ENGENHARIAS, SAÚDE E BEM ESTAR, DIREITO):
 * - Nº total de produtos canônicos
 * - Nº de produtos publicados vs sem modelagem
 * - Custo BOM médio da categoria
 * - Total de vendas acumuladas da categoria
 * - Cada produto linka para sua Ficha do Produto (#produto/:sku)
 */
export default function TelaCategoria({ categoryNameOrId, onBack, onOpenProduct }) {
  const { catalog, loading: loadingCatalog } = useCatalogContext();
  const [produtoDadosList, setProdutoDadosList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [loadingDados, setLoadingDados] = useState(true);
  const [search, setSearch] = useState("");
  const [isTaxonomiaModalOpen, setIsTaxonomiaModalOpen] = useState(false);

  // Carrega produto_dados e categorias do banco
  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setLoadingDados(true);
      try {
        const [dadosRecs, catRecs] = await Promise.all([
          pb.collection("produto_dados").getFullList({ expand: "categoria,tipo_joia" }),
          pb.collection("produto_categorias").getFullList({ sort: "nome" })
        ]);
        if (active) {
          setProdutoDadosList(dadosRecs || []);
          setCategoriesList(catRecs || []);
        }

        // Tenta carregar histórico de vendas para agregar por categoria
        try {
          const [ordersRes, pdvRes] = await Promise.all([
            pb.collection("production_orders").getList(1, 200, { filter: `archived != true` }),
            pb.collection("pdv_sales").getList(1, 200)
          ]);

          const allSales = [];
          ordersRes.items.forEach(o => {
            const sku = o.sku || o.full_data?.sku || "";
            const total = Number(o.total_value || o.full_data?.total_value || 0);
            if (sku) allSales.push({ skuPai: getBaseSku(sku), total });
          });

          pdvRes.items.forEach(p => {
            const sku = p.sku || p.full_data?.sku || "";
            const total = Number(p.total_amount || p.total || 0);
            if (sku) allSales.push({ skuPai: getBaseSku(sku), total });
          });

          if (active) setSalesList(allSales);
        } catch (e) {
          console.warn("Aviso ao ler vendas para agregados de categoria:", e);
        }

      } catch (err) {
        console.warn("Erro ao buscar dados de categoria em P4.1:", err);
      } finally {
        if (active) setLoadingDados(false);
      }
    };

    fetchData();
    return () => { active = false; };
  }, []);

  // Resolve o nome e ID da categoria atual (suporta id PB, categories_id iSet e nome)
  const activeCategory = useMemo(() => {
    if (!categoryNameOrId) return { id: null, categories_id: null, name: "Todas as Categorias" };

    const termUpper = String(categoryNameOrId).trim().toUpperCase();
    const matchById = categoriesList.find(c => c.id === categoryNameOrId || String(c.categories_id) === String(categoryNameOrId));
    if (matchById) return { id: matchById.id, categories_id: matchById.categories_id, name: matchById.nome };

    const matchByName = categoriesList.find(c => c.nome?.toUpperCase() === termUpper);
    if (matchByName) return { id: matchByName.id, categories_id: matchByName.categories_id, name: matchByName.nome };

    return { id: null, categories_id: null, name: categoryNameOrId };
  }, [categoryNameOrId, categoriesList]);

  // Agrupamento dos Produtos Canônicos da Categoria
  const categoryProducts = useMemo(() => {
    if (!catalog) return [];

    const dadosMapBySku = {};
    produtoDadosList.forEach(d => {
      if (d.sku_pai) dadosMapBySku[cleanSku(d.sku_pai)] = d;
    });

    const salesMapBySkuPai = {};
    salesList.forEach(s => {
      if (s.skuPai) {
        salesMapBySkuPai[s.skuPai] = (salesMapBySkuPai[s.skuPai] || 0) + s.total;
      }
    });

    const parentsMap = {};

    Object.values(catalog).forEach(item => {
      const rawSku = cleanSku(item.sku || item.full_data?.sku);
      if (!rawSku) return;

      const skuPai = getBaseSku(rawSku);
      if (parentsMap[skuPai]) return; // Já agrupado pelo pai

      const dadosRec = dadosMapBySku[skuPai] || null;
      const catName = dadosRec?.expand?.categoria?.nome || item.category || "Geral";

      // Verifica se pertence a esta categoria (por ID PocketBase, Nome ou categories_id iSet)
      const itemCatId = item.categories_id || item.full_data?.categories_id || item.full_data?.categories || item.full_data?.category_id;
      const isMatch = !activeCategory.id && activeCategory.name === "Todas as Categorias"
        ? true
        : (
            dadosRec?.categoria === activeCategory.id || 
            catName.toUpperCase() === activeCategory.name.toUpperCase() ||
            (activeCategory.categories_id && String(itemCatId).split(",").map(s=>s.trim()).includes(String(activeCategory.categories_id)))
          );

      if (isMatch) {
        const custoCentavos = dadosRec?.custo_bom_centavos || 0;
        const custoBRL = custoCentavos > 0 ? custoCentavos / 100 : 0;
        const precoVendaBRL = Number(item.price || item.full_data?.price || 0);

        parentsMap[skuPai] = {
          skuPai,
          name: item.name || `Produto ${skuPai}`,
          image: item.image || item.image_url || item.image_default || null,
          category: catName,
          estadoModelagem: dadosRec?.estado_modelagem || "sem_modelo",
          custoBRL,
          precoVendaBRL,
          vendasTotalBRL: salesMapBySkuPai[skuPai] || 0,
          dadosRecord: dadosRec,
          catalogItem: item
        };
      }
    });

    return Object.values(parentsMap);
  }, [catalog, produtoDadosList, salesList, activeCategory]);

  // Filtro de Busca Interna na Categoria
  const filteredProducts = useMemo(() => {
    if (!search.trim()) return categoryProducts;
    const term = cleanSku(search);
    return categoryProducts.filter(p => 
      p.skuPai.includes(term) || p.name.toUpperCase().includes(term)
    );
  }, [categoryProducts, search]);

  // ESTATÍSTICAS AGREGADAS DA CATEGORIA
  const categoryStats = useMemo(() => {
    const total = categoryProducts.length;
    const publicados = categoryProducts.filter(p => p.estadoModelagem === "publicado").length;
    const semModelo = categoryProducts.filter(p => p.estadoModelagem === "sem_modelo").length;
    const emModelagem = categoryProducts.filter(p => p.estadoModelagem === "em_modelagem" || p.estadoModelagem === "render_pendente").length;
    const prontos = categoryProducts.filter(p => p.estadoModelagem === "pronto").length;

    const somaCustos = categoryProducts.reduce((acc, p) => acc + p.custoBRL, 0);
    const custoMedioBRL = total > 0 ? somaCustos / total : 0;

    const totalVendasBRL = categoryProducts.reduce((acc, p) => acc + p.vendasTotalBRL, 0);

    return {
      total,
      publicados,
      semModelo,
      emModelagem,
      prontos,
      custoMedioBRL,
      totalVendasBRL
    };
  }, [categoryProducts]);

  const handleProductClick = (skuPai) => {
    if (onOpenProduct) {
      onOpenProduct(skuPai);
    } else {
      window.location.hash = `#produto/${skuPai}`;
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] w-full overflow-hidden bg-slate-50">
      
      {/* CABEÇALHO DA CATEGORIA */}
      <div className="bg-slate-900 text-white px-6 py-6 space-y-4 shrink-0 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          
          <div className="flex items-center gap-3">
            {onBack && (
              <button onClick={onBack} className="p-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-colors">
                <ArrowLeft size={18} />
              </button>
            )}
            <div className="p-2.5 bg-blue-600 rounded-xl text-white shadow-md">
              <Folder size={24} />
            </div>
            <div>
              <span className="text-[10px] uppercase font-bold tracking-wider text-blue-400">Visão da Categoria</span>
              <h1 className="text-xl font-bold tracking-tight">{activeCategory.name}</h1>
            </div>
          </div>

          {/* ESTATÍSTICAS RÁPIDAS DAS CATEGORIAS */}
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <div className="bg-slate-800 border border-slate-700 px-3.5 py-2 rounded-xl text-slate-300 font-medium">
              Produtos: <strong className="text-white font-mono">{categoryStats.total}</strong>
            </div>
            <div className="bg-emerald-950/80 border border-emerald-800 px-3.5 py-2 rounded-xl text-emerald-300 font-medium">
              Publicados: <strong className="text-emerald-200 font-mono">{categoryStats.publicados}</strong>
            </div>
            <div className="bg-amber-950/80 border border-amber-800 px-3.5 py-2 rounded-xl text-amber-300 font-medium">
              Sem Modelo: <strong className="text-amber-200 font-mono">{categoryStats.semModelo}</strong>
            </div>
            <div className="bg-blue-950/80 border border-blue-800 px-3.5 py-2 rounded-xl text-blue-300 font-medium">
              Custo Médio BOM: <strong className="text-blue-200 font-mono">{formatMoney(categoryStats.custoMedioBRL)}</strong>
            </div>
            <div className="bg-purple-950/80 border border-purple-800 px-3.5 py-2 rounded-xl text-purple-300 font-medium">
              Vendas da Categoria: <strong className="text-purple-200 font-mono">{formatMoney(categoryStats.totalVendasBRL)}</strong>
            </div>
            <button
              onClick={() => setIsTaxonomiaModalOpen(true)}
              className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow transition-colors shrink-0"
            >
              + Gerenciar Taxonomia
            </button>
          </div>

        </div>
      </div>

      {/* CONTROLE DE BUSCA INTERNA */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shrink-0">
        <div className="relative max-w-sm w-full">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Buscar em ${activeCategory.name}...`}
            className="w-full pl-9 pr-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white uppercase"
          />
        </div>

        <span className="text-xs font-semibold text-slate-500">
          Exibindo {filteredProducts.length} de {categoryProducts.length} produtos
        </span>
      </div>

      {/* GRID DE PRODUTOS DA CATEGORIA */}
      <div className="flex-1 overflow-y-auto p-6">
        {loadingCatalog || loadingDados ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
            <RefreshCw size={28} className="animate-spin text-blue-600" />
            <p className="text-sm font-medium">Carregando produtos da categoria...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="py-20 text-center text-slate-400 space-y-2">
            <Box size={44} className="mx-auto stroke-1 opacity-30" />
            <p className="text-base font-semibold text-slate-600">Nenhum produto cadastrado nesta categoria</p>
            <p className="text-xs text-slate-400">Verifique a busca ou associe produtos a esta categoria no catálogo.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredProducts.map((prod) => (
              <div
                key={prod.skuPai}
                onClick={() => handleProductClick(prod.skuPai)}
                className="bg-white border border-slate-200 hover:border-blue-400 rounded-xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer space-y-3 group flex flex-col justify-between"
              >
                <div className="space-y-3">
                  {/* IMAGEM E EMBLEMA DE STATUS */}
                  <div className="w-full h-36 bg-slate-100 border border-slate-200 rounded-lg overflow-hidden relative flex items-center justify-center">
                    {prod.image ? (
                      <img src={prod.image} alt={prod.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <Package size={32} className="text-slate-300" />
                    )}

                    <span className={`absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-bold shadow-sm ${
                      prod.estadoModelagem === "publicado"
                        ? "bg-emerald-600 text-white"
                        : prod.estadoModelagem === "pronto"
                        ? "bg-blue-600 text-white"
                        : prod.estadoModelagem === "em_modelagem"
                        ? "bg-amber-600 text-white"
                        : "bg-slate-700 text-slate-200"
                    }`}>
                      {prod.estadoModelagem}
                    </span>
                  </div>

                  {/* SKU & NOME */}
                  <div>
                    <span className="px-2 py-0.5 bg-slate-900 text-white rounded font-mono text-[10px] font-bold tracking-wider inline-block mb-1">
                      {prod.skuPai}
                    </span>
                    <h3 className="text-xs font-bold text-slate-800 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {prod.name}
                    </h3>
                  </div>
                </div>

                {/* CUSTO BOM E PREÇO */}
                <div className="border-t border-slate-100 pt-3 space-y-2">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 text-[11px]">Custo BOM:</span>
                    <span className="font-bold text-slate-700 font-mono">
                      {prod.custoBRL > 0 ? formatMoney(prod.custoBRL) : "N/D"}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 text-[11px]">Preço Venda:</span>
                    <span className="font-bold text-emerald-600 font-mono">
                      {prod.precoVendaBRL > 0 ? formatMoney(prod.precoVendaBRL) : "N/D"}
                    </span>
                  </div>

                  <div className="pt-1 flex items-center justify-between text-[11px] font-bold text-blue-600 group-hover:underline">
                    <span>Abrir Ficha Técnica</span>
                    <ArrowRight size={13} />
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL DE GERENCIAMENTO DE TAXONOMIA */}
      <TaxonomiaManagerModal
        isOpen={isTaxonomiaModalOpen}
        onClose={() => setIsTaxonomiaModalOpen(false)}
      />
    </div>
  );
}
